import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ChevronDown, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { TOOLBAR_CONTROL } from '@/components/ui/pageChrome';
import { TYPE, UI_MUTED, UI_SECTION } from '@/lib/typography';
import { readWorkingHours, useClinicUsersList } from '@/hooks/useClinicUsers';
import { useClinicRoles } from '@/hooks/useClinicRoles';
import { NO_PRACTITIONER_MATCH_ID } from '@/lib/practitionerRole';
import { useServicesList } from '@/hooks/useServices';
import { useSubClinics } from '@/hooks/useMultiClinic';
import { useInvoicePaymentMethods } from '@/hooks/useInvoices';
import { usePatientsByIds } from '@/hooks/usePatientPicker';
import { practitionerReportOptions } from '@/lib/reporting/practitionerOptions';
import {
  DEFAULT_METRIC_IDS,
  DEFAULT_PRESET_ID,
  PRACTITIONER_PRESETS,
  generatePractitionerReport,
} from '@/lib/reporting';
import {
  EMPTY_REPORT_FILTERS,
  clearAllReportFilters,
  clearMoreFilters,
  countActiveFilters,
} from '@/lib/reporting/filters';
import { resolveReportPeriod } from '@/lib/reporting/periods';
import { toWorkingContext } from '@/lib/reporting/workingHours';
import type { ReportFilterState } from '@/lib/reporting/types';
import {
  useDeleteReportTemplate,
  usePractitionerReportFacts,
  useReportTemplates,
  useSaveReportTemplate,
} from '@/hooks/usePractitionerReports';
import { MultiSelectFilter } from './MultiSelectFilter';
import { MetricSelector } from './MetricSelector';
import { MoreFiltersPopover } from './MoreFiltersPopover';
import { ReportResultView } from './ReportResultView';
import { cn } from '@/utils/cn';

const PERIODS: ReportFilterState['period'][] = [
  'today',
  'this_week',
  'this_month',
  'last_month',
  'this_year',
  'last_year',
  'custom',
];

function presetById(id: string) {
  return PRACTITIONER_PRESETS.find((item) => item.id === id) ?? PRACTITIONER_PRESETS[0];
}

export function PractitionerReportPage() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const defaultPreset = presetById(DEFAULT_PRESET_ID);
  const [presetId, setPresetId] = useState(DEFAULT_PRESET_ID);
  const [reportSource, setReportSource] = useState(`preset:${DEFAULT_PRESET_ID}`);
  const [filters, setFilters] = useState<ReportFilterState>({
    ...EMPTY_REPORT_FILTERS,
    grouping: defaultPreset.grouping,
  });
  const [staffScope, setStaffScope] = useState<'practitioners' | 'employees'>('practitioners');
  const [metricIds, setMetricIds] = useState<string[]>(DEFAULT_METRIC_IDS);
  const [metricsOpen, setMetricsOpen] = useState(false);
  const [requested, setRequested] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [saveOpen, setSaveOpen] = useState(false);

  const membersQ = useClinicUsersList(activeClinicId, { includeInactive: false });
  const rolesQ = useClinicRoles(activeClinicId);
  const servicesQ = useServicesList(activeClinicId, { includeInactive: true });
  const locationsQ = useSubClinics(activeClinicId);
  const paymentMethodsQ = useInvoicePaymentMethods(activeClinicId);
  const templatesQ = useReportTemplates(activeClinicId);
  const saveTemplate = useSaveReportTemplate(activeClinicId);
  const deleteTemplate = useDeleteReportTemplate(activeClinicId);
  const patientsQ = usePatientsByIds(activeClinicId, filters.patientIds);
  const factsQ = usePractitionerReportFacts(activeClinicId, filters, requested);

  const practitioners = useMemo(
    () =>
      practitionerReportOptions(membersQ.data ?? [], factsQ.data ?? [], {
        roles: rolesQ.isSuccess ? rolesQ.data : undefined,
        scope: staffScope,
        pinnedIds: filters.practitionerIds,
      }).map((person) => ({
        ...person,
        hours: person.hours == null ? null : readWorkingHours(person.hours),
      })),
    [
      factsQ.data,
      filters.practitionerIds,
      membersQ.data,
      rolesQ.data,
      rolesQ.isSuccess,
      staffScope,
    ],
  );

  const reportFilters = useMemo(() => {
    if (filters.practitionerIds.length > 0 || staffScope === 'employees' || !rolesQ.isSuccess) {
      return filters;
    }
    const ids = practitioners.map((person) => person.id);
    return {
      ...filters,
      practitionerIds: ids.length > 0 ? ids : [NO_PRACTITIONER_MATCH_ID],
    };
  }, [filters, practitioners, rolesQ.isSuccess, staffScope]);

  const serviceOptions = useMemo(
    () =>
      (servicesQ.data ?? [])
        .filter((service) => !service.is_blocker)
        .map((service) => ({ id: service.id, label: service.name })),
    [servicesQ.data],
  );

  const locationOptions = useMemo(() => {
    const subs = locationsQ.data ?? [];
    if (subs.length === 0) return [];
    return [
      ...(activeClinicId ? [{ id: activeClinicId, label: t('reports.filters.this_clinic') }] : []),
      ...subs.map((clinic) => ({ id: clinic.id, label: clinic.name })),
    ];
  }, [activeClinicId, locationsQ.data, t]);

  const patientLabels = useMemo(() => {
    const map: Record<string, string> = {};
    for (const patient of patientsQ.data ?? []) {
      map[patient.id] = `${patient.first_name} ${patient.last_name}`.trim();
    }
    return map;
  }, [patientsQ.data]);

  const period = useMemo(
    () => resolveReportPeriod(filters.period, filters.customFrom, filters.customTo),
    [filters.period, filters.customFrom, filters.customTo],
  );

  const workingHours = useMemo(
    () => practitioners.map((person) => toWorkingContext(person.id, person.hours, period)),
    [practitioners, period],
  );

  const builtResult = useMemo(() => {
    if (!requested || !factsQ.data) return null;
    return generatePractitionerReport({
      facts: factsQ.data,
      metricIds,
      filters: reportFilters,
      period,
      workingHours,
      unassignedPractitionerLabel: t('reports.unassigned_practitioner'),
      unassignedServiceLabel: t('reports.unassigned_service'),
      totalLabel: t('reports.total'),
      subtotalPrefix: t('reports.subtotal_prefix'),
    });
  }, [factsQ.data, metricIds, period, reportFilters, requested, t, workingHours]);

  const displayed = builtResult;
  const currentPreset = presetById(presetId);
  const hasFilterChanges = countActiveFilters(filters, { grouping: currentPreset.grouping }) > 0;

  const applyPreset = (nextPresetId: string) => {
    const preset = presetById(nextPresetId);
    setPresetId(preset.id);
    setReportSource(`preset:${preset.id}`);
    setMetricIds(preset.metricIds);
    setFilters((prev) => ({ ...prev, grouping: preset.grouping }));
    setMetricsOpen(false);
  };

  const onSaveTemplate = async () => {
    const name = templateName.trim();
    if (!name) return;
    try {
      await saveTemplate.mutateAsync({
        name,
        config: { metricIds, filters, presetId, practitionerScope: staffScope },
      });
      toast.success(t('reports.template_saved'));
      setSaveOpen(false);
      setTemplateName('');
    } catch {
      toast.error(t('reports.template_save_error'));
    }
  };

  const loadTemplate = (id: string) => {
    const template = templatesQ.data?.find((item) => item.id === id);
    if (!template?.config) return;
    const nextPresetId = template.config.presetId ?? DEFAULT_PRESET_ID;
    setPresetId(nextPresetId);
    setReportSource(`template:${id}`);
    setMetricIds(template.config.metricIds ?? presetById(nextPresetId).metricIds);
    setStaffScope(
      template.config.practitionerScope === 'employees' ? 'employees' : 'practitioners',
    );
    setFilters({
      ...EMPTY_REPORT_FILTERS,
      ...template.config.filters,
      grouping: template.config.filters.grouping ?? presetById(nextPresetId).grouping,
    });
    setMetricsOpen(false);
  };

  const onReportSelect = (value: string) => {
    if (value.startsWith('template:')) {
      loadTemplate(value.slice('template:'.length));
      return;
    }
    applyPreset(value.replace(/^preset:/, ''));
  };

  const contextParts = [
    t(`reports.periods.${filters.period}`),
    filters.practitionerIds.length === 0
      ? t(
          staffScope === 'employees'
            ? 'reports.filters.all_employees'
            : 'reports.filters.all_practitioners',
        )
      : filters.practitionerIds.length === 1
        ? (practitioners.find((person) => person.id === filters.practitionerIds[0])?.label ??
          t('reports.filters.practitioner'))
        : t('reports.filters.practitioners_count', { count: filters.practitionerIds.length }),
    filters.serviceIds.length === 0
      ? t('reports.filters.all_services')
      : filters.serviceIds.length === 1
        ? (serviceOptions.find((service) => service.id === filters.serviceIds[0])?.label ??
          t('reports.filters.services'))
        : t('reports.filters.services_count', { count: filters.serviceIds.length }),
  ];

  const chips: { id: string; label: string; onClear: () => void }[] = [];
  if (filters.practitionerIds.length === 0 && staffScope === 'employees') {
    chips.push({
      id: 'all-employees',
      label: t('reports.filters.all_employees'),
      onClear: () => setStaffScope('practitioners'),
    });
  }
  if (filters.practitionerIds.length > 0) {
    chips.push({
      id: 'practitioners',
      label:
        filters.practitionerIds.length === 1
          ? (practitioners.find((person) => person.id === filters.practitionerIds[0])?.label ??
            t('reports.filters.practitioner'))
          : t('reports.filters.practitioners_count', { count: filters.practitionerIds.length }),
      onClear: () => setFilters((prev) => ({ ...prev, practitionerIds: [] })),
    });
  }
  if (filters.serviceIds.length > 0) {
    chips.push({
      id: 'services',
      label:
        filters.serviceIds.length === 1
          ? (serviceOptions.find((service) => service.id === filters.serviceIds[0])?.label ??
            t('reports.filters.services'))
          : t('reports.filters.services_count', { count: filters.serviceIds.length }),
      onClear: () => setFilters((prev) => ({ ...prev, serviceIds: [] })),
    });
  }
  if (filters.locationIds.length > 0) {
    chips.push({
      id: 'locations',
      label:
        filters.locationIds.length === 1
          ? (locationOptions.find((item) => item.id === filters.locationIds[0])?.label ??
            t('reports.filters.location'))
          : t('reports.filters.locations_count', { count: filters.locationIds.length }),
      onClear: () => setFilters((prev) => ({ ...prev, locationIds: [] })),
    });
  }
  if (filters.appointmentStatus !== EMPTY_REPORT_FILTERS.appointmentStatus) {
    chips.push({
      id: 'status',
      label: t(`reports.filters.status_${filters.appointmentStatus}`),
      onClear: () =>
        setFilters((prev) => ({
          ...prev,
          appointmentStatus: EMPTY_REPORT_FILTERS.appointmentStatus,
        })),
    });
  }
  if (filters.revenueBasis !== EMPTY_REPORT_FILTERS.revenueBasis) {
    chips.push({
      id: 'basis',
      label: t(`reports.filters.basis_${filters.revenueBasis}`),
      onClear: () =>
        setFilters((prev) => ({ ...prev, revenueBasis: EMPTY_REPORT_FILTERS.revenueBasis })),
    });
  }
  if (filters.grouping !== currentPreset.grouping) {
    chips.push({
      id: 'grouping',
      label: t(`reports.grouping.${filters.grouping}`),
      onClear: () => setFilters((prev) => ({ ...prev, grouping: currentPreset.grouping })),
    });
  }
  for (const id of filters.patientIds) {
    chips.push({
      id: `patient-${id}`,
      label: patientLabels[id] ?? id,
      onClear: () =>
        setFilters((prev) => ({
          ...prev,
          patientIds: prev.patientIds.filter((item) => item !== id),
        })),
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/economy/reports"
            className={`inline-flex items-center ${UI_MUTED} hover:text-ds-text`}
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('reports.back')}
          </Link>
          <h2 className={`mt-2 ${UI_SECTION}`}>{t('reports.types.practitioner.label')}</h2>
          <p className={`mt-1 ${UI_MUTED}`}>{contextParts.join(' · ')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" onClick={() => setSaveOpen(true)}>
            {t('reports.save_as_template')}
          </Button>
          <Button
            onClick={() => setRequested(true)}
            disabled={!activeClinicId || factsQ.isFetching}
          >
            {factsQ.isFetching ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            {t('reports.generate')}
          </Button>
        </div>
      </div>

      <div className="flex w-full min-w-0 flex-wrap items-start justify-between gap-s3 nav:flex-nowrap nav:items-center">
        <div className="flex flex-wrap items-center gap-s3 nav:shrink-0">
          <label className="flex items-center gap-s2">
            <span className={UI_MUTED}>{t('reports.report_label')}</span>
            <select
              value={reportSource}
              onChange={(event) => onReportSelect(event.target.value)}
              className={cn(TOOLBAR_CONTROL, 'min-w-[13rem]')}
              aria-label={t('reports.report_label')}
            >
              {PRACTITIONER_PRESETS.map((preset) => (
                <option key={preset.id} value={`preset:${preset.id}`}>
                  {t(preset.labelKey)}
                </option>
              ))}
              {(templatesQ.data ?? []).length > 0 && (
                <optgroup label={t('reports.saved_templates')}>
                  {(templatesQ.data ?? []).map((template) => (
                    <option key={template.id} value={`template:${template.id}`}>
                      {template.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </label>
          <select
            value={filters.period}
            onChange={(event) =>
              setFilters((prev) => ({
                ...prev,
                period: event.target.value as ReportFilterState['period'],
              }))
            }
            className={TOOLBAR_CONTROL}
            aria-label={t('reports.export.period')}
          >
            {PERIODS.map((periodId) => (
              <option key={periodId} value={periodId}>
                {t(`reports.periods.${periodId}`)}
              </option>
            ))}
          </select>
          {filters.period === 'custom' ? (
            <>
              <input
                type="date"
                value={filters.customFrom ?? ''}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, customFrom: event.target.value || null }))
                }
                className={TOOLBAR_CONTROL}
              />
              <input
                type="date"
                value={filters.customTo ?? ''}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, customTo: event.target.value || null }))
                }
                className={TOOLBAR_CONTROL}
              />
            </>
          ) : null}
          <MultiSelectFilter
            label={t('reports.filters.practitioner')}
            allLabel={t(
              staffScope === 'employees'
                ? 'reports.filters.all_employees'
                : 'reports.filters.all_practitioners',
            )}
            headerOptions={[
              {
                id: 'practitioners',
                label: t('reports.filters.all_practitioners'),
                active: staffScope === 'practitioners' && filters.practitionerIds.length === 0,
                onSelect: () => {
                  setStaffScope('practitioners');
                  setFilters((prev) => ({ ...prev, practitionerIds: [] }));
                },
              },
              {
                id: 'employees',
                label: t('reports.filters.all_employees'),
                active: staffScope === 'employees' && filters.practitionerIds.length === 0,
                onSelect: () => {
                  setStaffScope('employees');
                  setFilters((prev) => ({ ...prev, practitionerIds: [] }));
                },
              },
            ]}
            options={practitioners}
            selectedIds={filters.practitionerIds}
            onChange={(ids) => setFilters((prev) => ({ ...prev, practitionerIds: ids }))}
            emptyLabel={t('reports.filters.no_practitioners')}
          />
          <MultiSelectFilter
            label={t('reports.filters.services')}
            allLabel={t('reports.filters.all_services')}
            options={serviceOptions}
            selectedIds={filters.serviceIds}
            onChange={(ids) => setFilters((prev) => ({ ...prev, serviceIds: ids }))}
            emptyLabel={t('reports.filters.no_services')}
          />
          <MoreFiltersPopover
            clinicId={activeClinicId}
            filters={filters}
            defaultGrouping={currentPreset.grouping}
            paymentMethods={paymentMethodsQ.data?.methods ?? []}
            locationOptions={locationOptions}
            patientLabels={patientLabels}
            onChange={setFilters}
            onClear={() =>
              setFilters((prev) => clearMoreFilters(prev, { grouping: currentPreset.grouping }))
            }
          />
        </div>
      </div>

      {(chips.length > 0 || hasFilterChanges) && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {chips.map((chip) => (
            <button
              key={chip.id}
              type="button"
              onClick={chip.onClear}
              className={cn(
                TYPE.body,
                'rounded-full border border-ds-line bg-ds-sunk px-2.5 py-0.5 text-ds-text hover:border-ds-field',
              )}
            >
              {chip.label} ×
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              setStaffScope('practitioners');
              setFilters({ ...clearAllReportFilters(), grouping: currentPreset.grouping });
              setRequested(false);
            }}
            className={`ml-1 ${UI_MUTED} hover:text-ds-text`}
          >
            {t('reports.clear_all')}
          </button>
        </div>
      )}

      <section className="rounded-card bg-ds-card shadow-card">
        <button
          type="button"
          onClick={() => setMetricsOpen((value) => !value)}
          className="flex w-full items-center justify-between px-4 py-2.5 text-left"
          aria-expanded={metricsOpen}
        >
          <span className={`${TYPE.body} text-ds-text`}>
            <span className="font-medium">{t('reports.metrics_label')}</span>
            <span className="mx-1.5 text-ds-muted">·</span>
            <span className="text-ds-muted">
              {t('reports.metrics_selected', { count: metricIds.length })}
            </span>
          </span>
          <ChevronDown
            className={cn(
              'h-4 w-4 text-ds-muted transition-transform',
              metricsOpen && 'rotate-180',
            )}
          />
        </button>
        {metricsOpen && (
          <div className="border-t border-ds-line px-4 py-3">
            <MetricSelector
              selectedIds={metricIds}
              onChange={setMetricIds}
              onReset={() => setMetricIds(currentPreset.metricIds)}
              t={t}
            />
          </div>
        )}
      </section>

      {factsQ.error && <ErrorAlert message={t('reports.load_error')} />}

      {requested && factsQ.isFetching && (
        <div className="flex items-center justify-center py-12 text-ds-muted">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      )}

      {displayed && !factsQ.isFetching && (
        <ReportResultView result={displayed} contextLabel={contextParts.join(' · ')} />
      )}

      {saveOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-ds-scrim p-4">
          <div className="w-full max-w-sm rounded-xl border border-ds-line-strong bg-ds-card p-s5 shadow-pop">
            <h3 className={UI_SECTION}>{t('reports.save_as_template')}</h3>
            <input
              value={templateName}
              onChange={(event) => setTemplateName(event.target.value)}
              className={cn(TOOLBAR_CONTROL, 'mt-3 w-full')}
              placeholder={t('reports.template_name')}
            />
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setSaveOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={onSaveTemplate}
                disabled={!templateName.trim() || saveTemplate.isPending}
              >
                {t('common.save')}
              </Button>
            </div>
            {(templatesQ.data ?? []).length > 0 && (
              <ul className={`mt-4 divide-y divide-ds-line ${TYPE.body}`}>
                {templatesQ.data!.map((template) => (
                  <li key={template.id} className="flex items-center justify-between py-2">
                    <span>{template.name}</span>
                    <button
                      type="button"
                      className={`${TYPE.body} text-ds-red-ink`}
                      onClick={() => deleteTemplate.mutate(template.id)}
                    >
                      {t('common.delete')}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
